'use client';

import { useEffect } from 'react';

/**
 * Global error boundary for the Next.js App Router.
 *
 * When a ChunkLoadError occurs (stale JS bundle after a Vercel deployment),
 * the page is automatically reloaded once so the browser fetches the new
 * bundle.  A reload-loop guard (sessionStorage flag) prevents infinite
 * refreshes if the error persists for a different reason.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isChunkError =
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed');

  useEffect(() => {
    if (isChunkError) {
      const reloadKey = 'cynthiaos_chunk_reload_attempted';
      const alreadyTried = sessionStorage.getItem(reloadKey);
      if (!alreadyTried) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
      }
    }
  }, [isChunkError]);

  // Clear the reload guard on successful navigation so future chunk errors
  // can also auto-recover.
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('cynthiaos_chunk_reload_attempted');
    };
  }, []);

  if (isChunkError) {
    return (
      <html data-component-id="src/app/global-error.tsx:47:6" data-component-path="src/app/global-error.tsx" data-component-line="47" data-component-end-line="65" data-component-file="global-error.tsx" data-component-name="html" data-component-content="%7B%22elementName%22%3A%22html%22%7D" lang="en">
        <body data-component-id="src/app/global-error.tsx:48:8" data-component-path="src/app/global-error.tsx" data-component-line="48" data-component-end-line="64" data-component-file="global-error.tsx" data-component-name="body" data-component-content="%7B%22elementName%22%3A%22body%22%7D"
          style={{
            background: '#0d0f14',
            color: '#c8cdd8',
            fontFamily: 'system-ui, sans-serif',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: '12px',
            textAlign: 'center',
            padding: '24px',
          }}
        >
          <p data-component-id="src/app/global-error.tsx:63:10" data-component-path="src/app/global-error.tsx" data-component-line="63" data-component-end-line="63" data-component-file="global-error.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22Refreshing%20to%20load%20the%20latest%20version%E2%80%A6%22%7D" style={{ fontSize: '1rem', opacity: 0.7 }}>Refreshing to load the latest version…</p>
        </body>
      </html>
    );
  }

  return (
    <html data-component-id="src/app/global-error.tsx:70:4" data-component-path="src/app/global-error.tsx" data-component-line="70" data-component-end-line="106" data-component-file="global-error.tsx" data-component-name="html" data-component-content="%7B%22elementName%22%3A%22html%22%7D" lang="en">
      <body data-component-id="src/app/global-error.tsx:71:6" data-component-path="src/app/global-error.tsx" data-component-line="71" data-component-end-line="105" data-component-file="global-error.tsx" data-component-name="body" data-component-content="%7B%22elementName%22%3A%22body%22%7D"
        style={{
          background: '#0d0f14',
          color: '#c8cdd8',
          fontFamily: 'system-ui, sans-serif',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: '16px',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <h2 data-component-id="src/app/global-error.tsx:86:8" data-component-path="src/app/global-error.tsx" data-component-line="86" data-component-end-line="86" data-component-file="global-error.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22textContent%22%3A%22Something%20went%20wrong%22%7D" style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Something went wrong</h2>
        <p data-component-id="src/app/global-error.tsx:87:8" data-component-path="src/app/global-error.tsx" data-component-line="87" data-component-end-line="89" data-component-file="global-error.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D" style={{ fontSize: '0.9rem', opacity: 0.6, margin: 0 }}>
          {error?.message ?? 'An unexpected error occurred.'}
        </p>
        <button data-component-id="src/app/global-error.tsx:90:8" data-component-path="src/app/global-error.tsx" data-component-line="90" data-component-end-line="104" data-component-file="global-error.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22textContent%22%3A%22Try%20again%22%7D"
          onClick={reset}
          style={{
            marginTop: '8px',
            padding: '8px 20px',
            background: '#5b5ef4',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          Try again
        </button>
      </body>
    </html>
  );
}
